import React, { useEffect, useState } from 'react';
import { Table, Container, Button, Group } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import { useNavigate } from 'react-router-dom';
import { Booking } from '../types/BookingModel';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLanguage } from '../providers/LanguageContext';

export const TAccount = ({ personId, readOnly = false }: { personId?: number; readOnly?: boolean }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const ta = t.tAccount;

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        
        const url = personId ? `/booking?personId=${personId}` : '/booking';
        const httpClient = new HttpClient<Booking>(url);
        const result: Booking[] = await httpClient.getAll();
        setBookings(
          result.slice().sort((a, b) => new Date(a.bookingDate).getTime() - new Date(b.bookingDate).getTime())
        );
      } catch (error) {
        console.error("API call failed:", error);
      }
      finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Group bookings by person
  const grouped = bookings.reduce((acc, booking) => {
    const personId = booking.person?.id ?? 'unknown';
    if (!acc[personId]) {
      acc[personId] = {
        person: booking.person,
        bookings: [],
      };
    }
    acc[personId].bookings.push(booking);
    return acc;
  }, {} as Record<string, { person: Booking['person'], bookings: Booking[] }>);

  const fmt = (v: number) => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleDownloadPdf = () => {
    const today = dayjs().format('DD.MM.YYYY');
    const doc = new jsPDF();

    Object.values(grouped).forEach(({ person, bookings }, index) => {
      if (index > 0) doc.addPage();

      const name = `${person?.lastName ?? ''} ${person?.firstName ?? ''}`.trim();
      const creditSum = bookings.filter(b => (b.bookingType?.multiplier ?? 0) > 0).reduce((s, b) => s + b.bookingAmount, 0);
      const debitSum  = bookings.filter(b => (b.bookingType?.multiplier ?? 0) < 0).reduce((s, b) => s + b.bookingAmount, 0);
      const net = creditSum - debitSum;

      doc.setFontSize(14);
      doc.text(name, 14, 16);
      doc.setFontSize(9);
      const ibanLine = person?.iban ? `IBAN: ${person.iban}` : null;
      if (ibanLine) doc.text(ibanLine, 14, 22);
      doc.text(`Stand: ${today}`, 14, ibanLine ? 27 : 22);

      autoTable(doc, {
        startY: ibanLine ? 33 : 28,
        head: [[ta.colDate, ta.colType, ta.colText, ta.colDebit, ta.colCredit]],
        body: [
          ...bookings.map(b => {
            const isCredit = (b.bookingType?.multiplier ?? 0) > 0;
            return [
              dayjs(b.bookingDate).format('DD.MM.YYYY HH:mm'),
              b.bookingType?.title ?? '',
              b.bookingText,
              !isCredit ? fmt(b.bookingAmount) : '',
              isCredit  ? fmt(b.bookingAmount) : '',
            ];
          }),
          ['', '', ta.sumLabel, fmt(debitSum), fmt(creditSum)],
          ['', '', net < 0 ? ta.liability : net > 0 ? ta.receivable : '',
            net < 0 ? fmt(Math.abs(net)) : '',
            net > 0 ? fmt(net) : ''],
        ],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [60, 60, 60] },
        columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' } },
        didParseCell: (data) => {
          if (data.row.index >= bookings.length) data.cell.styles.fontStyle = 'bold';
        },
      });
    });

    doc.save(`konto-${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Container style={{ overflowX: 'auto', padding: 0}}>
      <Group mb="sm" justify="flex-end">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconDownload size={14} />}
          onClick={handleDownloadPdf}
          disabled={bookings.length === 0}
        >
          PDF
        </Button>
      </Group>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{ta.colDate}</Table.Th>
            <Table.Th>{ta.colType}</Table.Th>
            <Table.Th>{ta.colText}</Table.Th>
            <Table.Th>{ta.colDebit}</Table.Th>
            <Table.Th>{ta.colCredit}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
{Object.values(grouped).map(({ person, bookings }) => {
  // Calculate sums
  const creditSum = bookings
    .filter(b => b.bookingType!.multiplier > 0)
    .reduce((sum, b) => sum + b.bookingAmount, 0);
  const debitSum = bookings
    .filter(b => b.bookingType!.multiplier < 0)
    .reduce((sum, b) => sum + b.bookingAmount, 0);

  return (
    <React.Fragment key={person?.id ?? 'unknown'}>
      {/* <Table.Tr>
        <Table.Td colSpan={5} style={{ fontWeight: 'bold' }}>
          {person?.firstName} {person?.lastName}
        </Table.Td>
      </Table.Tr> */}
      {bookings.map((booking) => (
        <Table.Tr key={booking.id} onClick={readOnly ? undefined : () => navigate(`/booking/edit/${booking.id}`)} style={readOnly ? undefined : { cursor: 'pointer' }}>
          <Table.Td>{dayjs(booking.bookingDate).format('DD.MM.YYYY HH:mm')}</Table.Td>
          <Table.Td>{booking?.bookingType?.title}</Table.Td>
          <Table.Td>{booking.bookingText}</Table.Td>
          <Table.Td align='right'>
            {booking.bookingType!.multiplier < 0 ? booking.bookingAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''}
          </Table.Td>
          <Table.Td align="right">
            {booking.bookingType!.multiplier > 0 ? booking.bookingAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''}
          </Table.Td>
        </Table.Tr>
      ))}
      {/* Sum row */}
      <Table.Tr style={{ fontWeight: 'bold' }}>
        <Table.Td colSpan={3} style={{ textAlign: 'right' }}>{ta.sumLabel}</Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>{debitSum.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Table.Td>
        <Table.Td align='right'>{creditSum.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</Table.Td>
      </Table.Tr>
      {/* Forderung/Verbindlichkeit row */}
      <Table.Tr style={{ fontWeight: 'bold' }}>
        <Table.Td colSpan={3} style={{ textAlign: 'right' }}>
          {creditSum - debitSum > 0
            ? ta.receivable
            : creditSum - debitSum < 0
              ? ta.liability
              : ''
          }
        </Table.Td>
        <Table.Td align='right'>
          {creditSum - debitSum > 0 ? (creditSum - debitSum).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''}
        </Table.Td>
        <Table.Td align='right'>
          {creditSum - debitSum < 0 ? Math.abs(creditSum - debitSum).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''}
        </Table.Td>
      </Table.Tr>
    </React.Fragment>
  );
})}
        </Table.Tbody>
      </Table>
    </Container>
  );
};